import FileRecord from "models/view-models/file-record";
import RemoteAccessDetailsService from "utilities/services/remote-access-details-service";
import SharedPageRemoteAccessDetailsService from "utilities/services/public/pages/shared-page-remote-access-details-service";
import { LocalStorageUtils } from "utilities/local-storage-utils";
import { useCallback } from "react";

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/** Non-shared page presigned url is valid for 1 day */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
/** Shared page presigned url is valid for 8 days */
const EIGHT_DAYS_MS = 8 * ONE_DAY_MS;

// Set the TTL to slightly less than the actual validity period to account for any delays in retrieval and usage of the URL
const ONE_DAY_TTL = ONE_DAY_MS - FIVE_MINUTES_MS;
const EIGHT_DAYS_TTL = EIGHT_DAYS_MS - FIVE_MINUTES_MS;

interface CachedImageUrl {
    url: string;
    expiresAt: number;
}

// #endregion Cache helpers

export const useImageFilePath = (
    imageFile?: FileRecord,
    pageGuid?: string,
    recipientGuid?: string
) => {
    const { get: getRemoteAccessDetails } =
        RemoteAccessDetailsService.useGetQuery();
    const { get: getSharedPageRemoteAccessDetails } =
        SharedPageRemoteAccessDetailsService.useGetQuery();

    const isSharedPage = pageGuid != null;
    const isValidImageFile =
        imageFile != null &&
        imageFile.storageContainer != null &&
        imageFile.relativeProviderPath != null;

    const cacheKey = isValidImageFile
        ? buildCacheKey(
              isSharedPage,
              imageFile.storageContainer!,
              imageFile.relativeProviderPath,
              pageGuid,
              recipientGuid
          )
        : undefined;

    const cachedUrl = cacheKey != null ? getCachedUrl(cacheKey) : undefined;

    const { data: remoteAccessDetailsResult } = getRemoteAccessDetails(
        undefined,
        {
            storageContainer: imageFile?.storageContainer,
            relativeProviderPath: imageFile?.relativeProviderPath!,
        },
        {
            enabled: !isSharedPage && isValidImageFile && cachedUrl == null,
        }
    );

    const { data: sharedPageRemoteAccessDetailsResult } =
        getSharedPageRemoteAccessDetails(
            {
                pageGuid: pageGuid!,
            },
            {
                recipientGuid,
                storageContainer: imageFile?.storageContainer,
                relativeProviderPath: imageFile?.relativeProviderPath!,
            },
            {
                enabled: isSharedPage && isValidImageFile && cachedUrl == null,
            }
        );

    const freshUrl =
        remoteAccessDetailsResult?.resultObject?.url ??
        sharedPageRemoteAccessDetailsResult?.resultObject?.url;

    if (cacheKey != null && freshUrl != null && cachedUrl == null) {
        const ttl = isSharedPage ? EIGHT_DAYS_TTL : ONE_DAY_TTL;
        setCachedUrl(cacheKey, freshUrl, ttl);
    }

    const cacheImageFilePath = useCallback(
        (fileToCache: FileRecord, url: string): void => {
            if (
                fileToCache.storageContainer == null ||
                fileToCache.relativeProviderPath == null ||
                url.length === 0
            ) {
                return;
            }

            const fileCacheKey = buildCacheKey(
                isSharedPage,
                fileToCache.storageContainer,
                fileToCache.relativeProviderPath,
                pageGuid,
                recipientGuid
            );
            const ttl = isSharedPage ? EIGHT_DAYS_TTL : ONE_DAY_TTL;

            setCachedUrl(fileCacheKey, url, ttl);
        },
        [isSharedPage, pageGuid, recipientGuid]
    );

    return {
        imageFilePath: cachedUrl ?? freshUrl ?? "",
        cacheImageFilePath,
    };
};

function buildCacheKey(
    isShared: boolean,
    storageContainer: string,
    relativeProviderPath: string,
    pageGuid?: string,
    recipientGuid?: string
): string {
    if (isShared) {
        return `image_url::shared::${pageGuid}/${recipientGuid}/${storageContainer}/${relativeProviderPath}`;
    }
    return `image_url::regular::${storageContainer}/${relativeProviderPath}`;
}

function getCachedUrl(key: string): string | undefined {
    const entry = LocalStorageUtils.get<CachedImageUrl>(key);
    if (entry != null && Date.now() < entry.expiresAt) {
        return entry.url;
    }
    return undefined;
}

function setCachedUrl(key: string, url: string, ttlMs: number): void {
    LocalStorageUtils.set<CachedImageUrl>(key, {
        url,
        expiresAt: Date.now() + ttlMs,
    });
}
